import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Tooltip,
  InputAdornment,
  SelectChangeEvent,
} from "@mui/material";
import {
  Add,
  Edit,
  Refresh,
  Search,
  Clear,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { callApi } from "../../../utils/apiHelper";
import { apiEndpoints } from "../../../utils/apiEndpoints";
import {
  Sku,
  RawMaterial,
  UnitConversion,
  UnitType,
  unitCategoryMap,
  getAllSkus,
  getRawMaterials
} from "../../../utils/commonApis";
import UnitConverter from "../../UnitConverter";
import CreateEditSku from "./create_Edit_sku";

const SkuManagement: React.FC = () => {
  // State variables
  const [skus, setSkus] = useState<Sku[]>([]);
  const [filteredSkus, setFilteredSkus] = useState<Sku[]>([]);
  const [rawMaterials, setRawMaterials] = useState<RawMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [openSkuForm, setOpenSkuForm] = useState(false);
  const [openImagesDialog, setOpenImagesDialog] = useState(false);
  const [currentSku, setCurrentSku] = useState<Sku | null>(null);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [visibilityFilter, setVisibilityFilter] = useState<string>("all");
  const [alert, setAlert] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  // Fetch all SKUs and raw materials when component mounts
  useEffect(() => {
    fetchSkus();
    fetchRawMaterials();
  }, []);

  const fetchSkus = async () => {
    setLoading(true);
    try {
      const response = await getAllSkus();
      if (response.data && response.data.skus) {
        setSkus(response.data.skus);
        setFilteredSkus(response.data.skus);
      }
    } catch (error) {
      console.error("Error fetching SKUs:", error);
      showAlert("Failed to fetch SKUs", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchRawMaterials = async () => {
    try {
      const response = await getRawMaterials();
      if (response && response.rawMaterials) {
        setRawMaterials(response.rawMaterials);
      }
    } catch (error) {
      console.error("Error fetching raw materials:", error);
    }
  };

  useEffect(() => {
    applyFilters();
  }, [skus, searchTerm, statusFilter, priorityFilter, visibilityFilter]);

  const applyFilters = () => {
    let result = [...skus];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (sku) =>
          sku.Name.toLowerCase().includes(term) ||
          sku.Code.toLowerCase().includes(term) ||
          sku.Description.toLowerCase().includes(term)
      );
    }

    // Apply status filter
    if (statusFilter !== "all") {
      result = result.filter((sku) => sku.Status === statusFilter);
    }

    // Apply priority filter
    if (priorityFilter !== "all") {
      const priority = parseInt(priorityFilter);
      result = result.filter((sku) => sku.Priority === priority);
    }

    // Apply visibility filter
    if (visibilityFilter !== "all") {
      const visible = visibilityFilter === "visible";
      result = result.filter((sku) => sku.Visibility === visible);
    }

    setFilteredSkus(result);
  };

  const handleOpenCreateForm = () => {
    setCurrentSku(null);
    setFormMode("create");
    setOpenSkuForm(true);
  };

  const handleOpenEditForm = (sku: Sku) => {
    setCurrentSku(sku);
    setFormMode("edit");
    setOpenSkuForm(true);
  };

  const handleOpenImagesDialog = (sku: Sku) => {
    setCurrentSku(sku);
    setOpenImagesDialog(true);
  };

  const handleCloseForm = () => {
    setOpenSkuForm(false);
    setCurrentSku(null);
  };

  const handleCloseImagesDialog = () => {
    setOpenImagesDialog(false);
  };

  const handleRefresh = () => {
    fetchSkus();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  const handleStatusFilterChange = (e: SelectChangeEvent) => {
    setStatusFilter(e.target.value);
  };

  const handlePriorityFilterChange = (e: SelectChangeEvent) => {
    setPriorityFilter(e.target.value);
  };

  const handleVisibilityFilterChange = (e: SelectChangeEvent) => {
    setVisibilityFilter(e.target.value);
  };

  const toggleSkuVisibility = async (sku: Sku) => {
    try {
      const response = await callApi(
        apiEndpoints.NulookSkuManagement,
        "POST",
        {
          operation: "update",
          SkuId: sku.SkuId,
          Visibility: !sku.Visibility,
        }
      );
      
      if (response.data) {
        // Update local state
        const updatedSkus = skus.map((s) => 
          s.SkuId === sku.SkuId ? { ...s, Visibility: !s.Visibility } : s
        );
        setSkus(updatedSkus);
        
        showAlert(
          `SKU ${sku.Visibility ? "hidden" : "visible"} successfully`,
          "success"
        );
      } else {
        showAlert("Failed to update SKU visibility", "error");
      }
    } catch (error) {
      console.error("Error updating SKU visibility:", error);
      showAlert("Failed to update SKU visibility", "error");
    }
  };

  const handleFormSubmitSuccess = () => {
    handleCloseForm();
    fetchSkus();
    showAlert(
      `SKU ${formMode === "create" ? "created" : "updated"} successfully`,
      "success"
    );
  };

  const showAlert = (message: string, severity: "success" | "error" | "info" | "warning") => {
    setAlert({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  // Format timestamp to readable date
  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  // Get raw material name by ID
  const getRawMaterialName = (id: string) => {
    const material = rawMaterials.find((m) => m.RawMaterialId === id);
    return material ? material.Name : id;
  };

  // Render SKU material limits
  const renderSkuMaterialLimits = (sku: Sku) => {
    if (!sku.SkuPerPieceLimit || Object.keys(sku.SkuPerPieceLimit).length === 0) {
      return <Typography variant="body2">No material limits set</Typography>;
    }

    return (
      <Box sx={{ maxHeight: "300px", overflow: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Raw Material</TableCell>
              <TableCell>Limit Per Piece</TableCell>
              <TableCell>Unit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(sku.SkuPerPieceLimit).map(([materialId, limit]) => (
              <TableRow key={materialId}>
                <TableCell>{getRawMaterialName(materialId)}</TableCell>
                <TableCell>{limit}</TableCell>
                <TableCell>
                  {rawMaterials.find((m) => m.RawMaterialId === materialId)?.BaseUnit || "-"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  };

  // Render SKU status chip with appropriate color
  const renderStatusChip = (status: string) => {
    let color: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" = "default";
    
    switch (status.toLowerCase()) {
      case "active":
        color = "success";
        break;
      case "inactive":
        color = "error";
        break;
      case "pending":
        color = "warning";
        break;
      case "draft":
        color = "info";
        break;
      default:
        color = "default";
    }
    
    return <Chip label={status} color={color} size="small" />;
  };

  // Render SKU images dialog
  const renderImagesDialog = () => {
    if (!currentSku) return null;
    
    return (
      <Dialog open={openImagesDialog} onClose={handleCloseImagesDialog} maxWidth="md" fullWidth>
        <DialogTitle>Images for {currentSku.Name}</DialogTitle>
        <DialogContent>
          {currentSku.Images && currentSku.Images.length > 0 ? (
            <Grid container spacing={2}>
              {currentSku.Images.map((image, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box
                    component="img"
                    src={image}
                    alt={`${currentSku.Name} - Image ${index + 1}`}
                    sx={{
                      width: "100%",
                      height: "200px",
                      objectFit: "cover",
                      borderRadius: 1,
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body1">No images available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImagesDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        SKU Management
      </Typography>
      
      {/* Actions and filters section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Search SKUs"
              value={searchTerm}
              onChange={handleSearchChange}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={clearSearch}>
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={handleStatusFilterChange}
                label="Status"
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                value={priorityFilter}
                onChange={handlePriorityFilterChange}
                label="Priority"
              >
                <MenuItem value="all">All Priorities</MenuItem>
                <MenuItem value="1">1 (Highest)</MenuItem>
                <MenuItem value="2">2</MenuItem>
                <MenuItem value="3">3</MenuItem>
                <MenuItem value="4">4</MenuItem>
                <MenuItem value="5">5 (Lowest)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Visibility</InputLabel>
              <Select
                value={visibilityFilter}
                onChange={handleVisibilityFilterChange}
                label="Visibility"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="visible">Visible</MenuItem>
                <MenuItem value="hidden">Hidden</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={6} sm={1.5}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
            >
              Refresh
            </Button>
          </Grid>
          
          <Grid item xs={6} sm={1.5}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={handleOpenCreateForm}
            >
              Add SKU
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* SKUs table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Base Unit</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredSkus.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No SKUs found
                </TableCell>
              </TableRow>
            ) : (
              filteredSkus.map((sku) => (
                <TableRow 
                  key={sku.SkuId}
                  sx={{ 
                    opacity: sku.Visibility ? 1 : 0.6,
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                  }}
                >
                  <TableCell>{sku.Code}</TableCell>
                  <TableCell>{sku.Name}</TableCell>
                  <TableCell>{sku.BaseUnit}</TableCell>
                  <TableCell>₹{sku.Price.toFixed(2)}</TableCell>
                  <TableCell>{renderStatusChip(sku.Status)}</TableCell>
                  <TableCell>{sku.Priority}</TableCell>
                  <TableCell>{formatDate(sku.UpdatedAt)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Edit SKU">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenEditForm(sku)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="View Images">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => handleOpenImagesDialog(sku)}
                          disabled={!sku.Images || sku.Images.length === 0}
                        >
                          <ImageIcon />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title={sku.Visibility ? "Hide SKU" : "Show SKU"}>
                        <IconButton
                          size="small"
                          color={sku.Visibility ? "default" : "success"}
                          onClick={() => toggleSkuVisibility(sku)}
                        >
                          {sku.Visibility ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* SKU Form Dialog */}
      <Dialog open={openSkuForm} onClose={handleCloseForm} maxWidth="md" fullWidth>
        <DialogTitle>
          {formMode === "create" ? "Create New SKU" : `Edit SKU: ${currentSku?.Name}`}
        </DialogTitle>
        <DialogContent dividers>
          <CreateEditSku
            mode={formMode}
            sku={currentSku}
            rawMaterials={rawMaterials}
            onSubmitSuccess={handleFormSubmitSuccess}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm}>Cancel</Button>
        </DialogActions>
      </Dialog>
      
      {/* Images Dialog */}
      {renderImagesDialog()}
      
      {/* Alert Snackbar */}
      <Snackbar
        open={alert.open}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: "100%" }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SkuManagement;